'use client'

import { getProductStockSellingPrices } from '@/lib/actions/products/get-product-stock-selling-prices'
import { useAppSelector, useCart, useSearchParams } from '@/lib/hooks'
import { cn, formatPrice, handleNumberInput } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Minus, Plus } from 'lucide-react'
import { useSession } from 'next-auth/react'
import React from 'react'
import { BagCheck } from 'solar-icons'
import { toast } from 'sonner'

import {
  Badge,
  Barcode,
  Button,
  Checkbox,
  Input,
  KeyChip,
  Label,
  LoadingDots,
  RadioGroup,
  RadioGroupItem,
  Separator,
  Thumbnail,
} from '@/components/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface AddToCartProps extends React.ComponentPropsWithoutRef<'form'> {
  product: Product
}

export const AddToCart = ({ product }: AddToCartProps) => {
  const { data: session }: any = useSession()
  const { cartId } = useSearchParams()
  const { addToCart } = useCart() as any

  const [qty, setQty] = React.useState(1)
  const [selectedPrice, setSelectedPrice] = React.useState<any>(null)
  const [selectedPromo, setSelectedPromo] = React.useState<any>('')
  const [sellingPrices, setSellingPrices] = React.useState<any>(null)
  const [addons, setAddons] = React.useState<any>(null)
  const [selectedAddons, setSelectedAddons] = React.useState<any>({})
  const [customPrice, setCustomPrice] = React.useState<any>(null)

  const { data: stockPrices, isPending: isFetchingStockPrices } = useQuery({
    queryKey: ['product-stock-selling-prices', product?.id],
    queryFn: () => getProductStockSellingPrices(product),
  })

  React.useEffect(() => {
    if (stockPrices) {
      setAddons(stockPrices?.product?.add_ons)
      setSellingPrices(stockPrices)
      setSelectedPrice(stockPrices?.selling_prices?.[0]?.id)
      setSelectedPromo('')
    }
  }, [stockPrices])

  const validateAddons = () => {
    /**
     * Check for `is_required` addons in product?.add_on_categories array
     * Check if selectedAddons has any `is_required` addons
     * If not, show a toast error
     * If all `is_required` addons are selected, continue
     */

    // Get all required addon categories
    const requiredCategories = product?.add_on_categories?.filter(
      (category: any) => category?.is_required,
    )

    // If no required categories, validation passes
    if (!requiredCategories || requiredCategories.length === 0) {
      return true
    }

    // Check if all required categories have at least one addon selected
    const missingCategories = requiredCategories.filter((category: any) => {
      const categoryAddons = selectedAddons[category?.id]
      return !categoryAddons || categoryAddons.length === 0
    })

    // If there are missing required categories, show error
    if (missingCategories.length > 0) {
      const categoryNames = missingCategories
        .map((cat: any) => cat?.name)
        .join(', ')

      toast.error(`Please select at least one addon from: ${categoryNames}`)
      return false
    }

    return true
  }

  const handleSubmit = async (data: FormData) => {
    const stock_selling_price_id = Number(data.get('stock-price'))
    const qty = Number(data.get('qty'))
    const promotion_item_id = data.get('selected-promotion')
      ? Number(data.get('selected-promotion'))
      : null
    const customPrice = data.get('customPrice')

    if (Number(customPrice) && Number(customPrice) < 1) {
      toast.error('Custom price must be greater than 0')
      return
    }

    // Validate required addons
    if (!validateAddons()) {
      return
    }

    // Transform selectedAddons object to array of {id, qty} objects
    const addonsWithQuantity = Object.values(selectedAddons)
      .flat()
      .map((addon: any) => ({
        id: addon?.addon_price_id,
        qty: addon?.quantity || 1,
      }))

    const customPriceValue = Number(customPrice) ?? null
    const barcode = product?.default_price?.barcode

    const customPricePayload = {
      stock_selling_price_id: customPriceValue ? null : stock_selling_price_id,
      price: customPriceValue ?? null,
      barcode,
      variant_id: product?.variant_id,
      qty,
      promotion_item_id,
      addon_price_ids: addonsWithQuantity,
    }

    const stockSellingPricePayload = {
      stock_selling_price_id,
      promotion_item_id,
      addon_price_ids: addonsWithQuantity,
      qty,
    }

    console.log({
      cartId,
      product: customPriceValue ? customPricePayload : stockSellingPricePayload,
    })

    await addToCart({
      cartId,
      product: customPriceValue ? customPricePayload : stockSellingPricePayload,
    })
  }

  return (
    <form
      className="flex w-full flex-col flex-wrap gap-4 lg:gap-8"
      action={handleSubmit}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
        <Thumbnail
          size="lg"
          src={product?.image_url}
          className="border border-gray-100"
        />

        <div className="flex w-full flex-col flex-wrap gap-4">
          <h2 className="max-w-[396px] text-balance text-xl font-medium">
            {product?.variant_label ?? product?.title}
          </h2>

          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-4 md:flex-row">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="w-[36px] px-0" size="md">
                  SKU
                </Badge>
                <span className="font-mono text-sm font-medium text-gray-950">
                  {product?.sku}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="min-w-5 px-1" size="md">
                  <Barcode className="h-3" />
                </Badge>
                <span className="font-mono text-sm font-medium text-gray-950">
                  {product?.default_price?.barcode}
                </span>
              </div>
            </div>
          </div>

          {!isFetchingStockPrices && (
            <div className="flex h-auto w-full flex-col gap-5">
              {sellingPrices?.selling_prices?.length > 0 && (
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full items-center gap-2">
                    <Label className="shrink-0 !text-sm font-medium text-gray-700">
                      Selling Price
                    </Label>
                    <Separator className="mt-1" />
                  </div>

                  <div className="flex w-full flex-col overflow-y-auto lg:max-h-[200px]">
                    <RadioGroup
                      className="flex w-full flex-col gap-1.5"
                      name="stock-price"
                      value={selectedPrice}
                      onValueChange={(value) => setSelectedPrice(value)}
                    >
                      {sellingPrices?.selling_prices?.map(
                        (price: any, index: number) => {
                          return (
                            <div
                              key={price?.id}
                              className={cn(
                                'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 shadow-card hover:bg-gray-50 lg:flex-row',
                                {
                                  'border-gray-500':
                                    price?.id === selectedPrice,
                                },
                              )}
                            >
                              <RadioGroupItem
                                value={price?.id}
                                id={`price-${price?.id}`}
                                className="absolute inset-y-0 right-2 my-auto"
                              />
                              <Label
                                htmlFor={`price-${price?.id}`}
                                className="w-full cursor-auto px-3 py-3 font-mono !text-base font-semibold"
                              >
                                {formatPrice(price?.amount, 2, true)}
                              </Label>

                              {price?.stock?.expiry_date && (
                                <div className="hidden shrink-0 flex-col items-end pr-11 text-right lg:flex">
                                  <span className="shrink-0 text-xs text-gray-700">
                                    Expires on
                                  </span>
                                  <span className="shrink-0 text-xs font-medium text-gray-950">
                                    {price?.stock?.expiry_date}
                                  </span>
                                </div>
                              )}
                            </div>
                          )
                        },
                      )}
                    </RadioGroup>
                  </div>
                </div>
              )}

              {session?.customPrice === '1' && (
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full items-center gap-2">
                    <Label className="shrink-0 !text-sm font-medium text-gray-700">
                      Custom Price
                    </Label>
                    <Separator className="mt-1" />
                  </div>
                  <div className="flex">
                    <Input
                      className="w-full appearance-none text-center"
                      value={customPrice ? customPrice : ''}
                      inputSize="lg"
                      min={1}
                      name="customPrice"
                      onChangeValue={(value) => {
                        setCustomPrice(value)
                      }}
                      onKeyDown={handleNumberInput}
                      type="number"
                      placeholder="Enter custom price"
                    />
                  </div>
                </div>
              )}

              {sellingPrices?.promotion_items?.length > 0 && (
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full items-center gap-2">
                    <Label className="shrink-0 !text-sm font-medium text-gray-700">
                      Promotions
                    </Label>
                    <Separator className="mt-1" />
                  </div>

                  <div className="flex w-full flex-col gap-1.5">
                    <input
                      type="hidden"
                      name="selected-promotion"
                      value={selectedPromo ?? ''}
                    />
                    {sellingPrices?.promotion_items?.map(
                      (promo: any, index: number) => {
                        const isSelected = promo?.id === selectedPromo
                        return (
                          <div
                            key={index}
                            className={cn(
                              'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 shadow-card hover:bg-gray-50 lg:flex-row',
                              {
                                'border-gray-500': isSelected,
                              },
                            )}
                          >
                            <Checkbox
                              className="absolute inset-y-0 right-2 my-auto"
                              id={`promo-${promo?.id}`}
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                setSelectedPromo(checked ? promo?.id : '')
                              }}
                            />
                            <Label
                              htmlFor={`promo-${promo?.id}`}
                              className="flex w-full cursor-pointer items-center justify-between px-3 py-3 pr-10 !text-sm font-semibold"
                            >
                              <span>{promo?.title}</span>
                              <span>
                                {promo?.value}
                                {promo?.type === 'percentage' && '%'}
                              </span>
                            </Label>
                          </div>
                        )
                      },
                    )}
                  </div>
                </div>
              )}

              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-2">
                  <Label className="shrink-0 !text-sm font-medium text-gray-700">
                    Quantity
                  </Label>
                  <Separator className="mt-1" />
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    className="max-w-[200px] appearance-none text-center"
                    key={qty}
                    value={qty}
                    inputSize="lg"
                    min={1}
                    name="qty"
                    onChangeValue={(value) => {
                      setQty(Number(value))
                    }}
                    onKeyDown={handleNumberInput}
                    type="number"
                    autoFocus
                  />
                  <div className="flex w-full items-center gap-2">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        if (qty > 1) {
                          setQty((prevQty) => prevQty - 1)
                        }
                      }}
                      type="button"
                    >
                      <Minus className="size-5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setQty((prevQty) => prevQty + 1)
                      }}
                      type="button"
                    >
                      <Plus className="size-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isFetchingStockPrices && (
            <div className="flex h-full w-full items-center justify-center pr-8 lg:h-[360px]">
              <LoadingDots />
            </div>
          )}
        </div>
      </div>

      {addons?.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-center gap-2">
            <Label className="shrink-0 !text-sm font-medium text-gray-700">
              Addons
            </Label>
            <Separator className="mt-1" />
          </div>

          <Tabs
            defaultValue={product?.categorized_add_ons?.[0]?.category_id}
            className="grid w-full grid-cols-1 gap-4 lg:grid-cols-[200px_1fr] lg:gap-8"
          >
            <TabsList className="m-0 h-auto max-h-[240px] flex-col items-start justify-start gap-1 overflow-y-auto border border-gray-100 bg-transparent p-3">
              {product?.categorized_add_ons.map((category: any) => {
                return (
                  <TabsTrigger
                    key={category?.category_id}
                    value={category?.category_id}
                    className="h-auto min-h-10 w-full"
                  >
                    <div className="w-full hyphens-auto whitespace-normal break-words text-left">
                      {category?.category_name}
                    </div>
                  </TabsTrigger>
                )
              })}
            </TabsList>

            <div className="flex max-h-[240px] w-full flex-col overflow-y-auto rounded-md border border-gray-100 bg-gray-50/20 p-3">
              {product?.categorized_add_ons.map((category: any) => {
                return (
                  <TabsContent
                    key={category?.category_id}
                    value={category?.category_id}
                    className="m-0 flex w-full flex-col gap-1 p-0"
                  >
                    {category?.add_ons.map((addon: any) => {
                      if (!addon?.default_price) return null

                      const hasQuantity =
                        (selectedAddons[category?.category_id]?.find(
                          (item: any) =>
                            item.addon_price_id ===
                            addon?.default_price?.price_id,
                        )?.quantity ?? 0) > 0

                      return (
                        <div
                          key={addon?.id}
                          className={cn(
                            'relative flex w-full cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-gray-100 bg-white shadow-card hover:bg-gray-50 lg:flex-row',
                            {
                              'border-gray-500': hasQuantity,
                            },
                          )}
                        >
                          <Checkbox
                            className={cn(
                              'absolute inset-y-0 right-3 my-auto',
                              {
                                hidden: hasQuantity,
                              },
                            )}
                            value={addon?.default_price?.price_id}
                            id={`addon-${addon?.default_price?.price_id}`}
                            checked={
                              selectedAddons[category?.category_id]?.some(
                                (item: any) =>
                                  item.addon_price_id ===
                                  addon?.default_price?.price_id,
                              ) || false
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                // Check max_limit before adding new addon
                                const categoryInfo =
                                  product?.add_on_categories?.find(
                                    (cat: any) =>
                                      cat?.id === category?.category_id,
                                  )

                                if (categoryInfo?.max_limit) {
                                  // Calculate total quantity of all addons in this category
                                  const currentTotal =
                                    selectedAddons[
                                      category?.category_id
                                    ]?.reduce(
                                      (sum: number, item: any) =>
                                        sum + (item.quantity || 1),
                                      0,
                                    ) || 0

                                  if (currentTotal >= categoryInfo.max_limit) {
                                    toast.error(
                                      `Maximum ${categoryInfo.max_limit} item${categoryInfo.max_limit > 1 ? 's' : ''} allowed for ${category?.category_name}`,
                                    )
                                    return
                                  }
                                }

                                setSelectedAddons((prev: any) => ({
                                  ...prev,
                                  [category?.category_id]: [
                                    ...(prev[category?.category_id] || []),
                                    {
                                      addon_price_id:
                                        addon?.default_price?.price_id,
                                      addon_id: addon?.id,
                                      quantity: 1,
                                    },
                                  ],
                                }))
                              } else {
                                // Prevent unchecking if quantity > 1
                                const currentItem = selectedAddons[
                                  category?.category_id
                                ]?.find(
                                  (item: any) =>
                                    item.addon_price_id ===
                                    addon?.default_price?.price_id,
                                )

                                if (currentItem && currentItem.quantity > 0) {
                                  // toast.info(
                                  //   'Please reduce quantity to 1 before removing this item',
                                  // )
                                  return
                                }

                                setSelectedAddons((prev: any) => {
                                  const categoryAddons = prev[
                                    category?.category_id
                                  ]?.filter(
                                    (item: any) =>
                                      item.addon_price_id !==
                                      addon?.default_price?.price_id,
                                  )

                                  // If no addons left in category, remove the category key
                                  if (
                                    !categoryAddons ||
                                    categoryAddons.length === 0
                                  ) {
                                    const {
                                      [category?.category_id]: _,
                                      ...rest
                                    } = prev
                                    return rest
                                  }

                                  return {
                                    ...prev,
                                    [category?.category_id]: categoryAddons,
                                  }
                                })
                              }
                            }}
                          />
                          <Label
                            htmlFor={`addon-${addon?.default_price?.price_id}`}
                            className={cn(
                              'flex w-full cursor-auto items-center justify-between p-2 !text-sm font-semibold',
                              {
                                'pr-2': hasQuantity,
                                'pr-12': !hasQuantity,
                              },
                              // {
                              //   'pointer-events-none':
                              //     (selectedAddons[category?.category_id]?.find(
                              //       (item: any) =>
                              //         item.addon_price_id ===
                              //         addon?.default_price?.price_id,
                              //     )?.quantity || 1) > 1,
                              // },
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <Thumbnail
                                src={addon?.image_url}
                                className="size-8 rounded-md"
                              />
                              <div className="flex flex-col">
                                <span>{addon?.name}</span>
                                <span className="text-sm font-medium text-gray-500">
                                  {formatPrice(
                                    addon?.default_price?.price,
                                    2,
                                    true,
                                  )}
                                </span>
                              </div>
                            </div>

                            {selectedAddons[category?.category_id]?.some(
                              (item: any) =>
                                item.addon_price_id ===
                                addon?.default_price?.price_id,
                            ) && (
                                <div className="flex items-center">
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    type="button"
                                    className="size-9"
                                    onClick={(e) => {
                                      e.preventDefault()

                                      const currentItem = selectedAddons[
                                        category?.category_id
                                      ]?.find(
                                        (item: any) =>
                                          item.addon_price_id ===
                                          addon?.default_price?.price_id,
                                      )

                                      // If quantity is 1, remove the item entirely
                                      if ((currentItem?.quantity || 1) === 1) {
                                        setSelectedAddons((prev: any) => {
                                          const categoryAddons = prev[
                                            category?.category_id
                                          ]?.filter(
                                            (item: any) =>
                                              item.addon_price_id !==
                                              addon?.default_price?.price_id,
                                          )

                                          // If no addons left in category, remove the category key
                                          if (
                                            !categoryAddons ||
                                            categoryAddons.length === 0
                                          ) {
                                            const {
                                              [category?.category_id]: _,
                                              ...rest
                                            } = prev
                                            return rest
                                          }

                                          return {
                                            ...prev,
                                            [category?.category_id]:
                                              categoryAddons,
                                          }
                                        })
                                      } else {
                                        // Otherwise, just decrease the quantity
                                        setSelectedAddons((prev: any) => ({
                                          ...prev,
                                          [category?.category_id]: prev[
                                            category?.category_id
                                          ].map((item: any) =>
                                            item.addon_price_id ===
                                              addon?.default_price?.price_id
                                              ? {
                                                ...item,
                                                quantity:
                                                  (item.quantity || 1) - 1,
                                              }
                                              : item,
                                          ),
                                        }))
                                      }
                                    }}
                                  >
                                    <Minus className="size-4" strokeWidth={1.5} />
                                  </Button>

                                  <span className="w-[28px] text-center">
                                    {selectedAddons[category?.category_id]?.find(
                                      (item: any) =>
                                        item.addon_price_id ===
                                        addon?.default_price?.price_id,
                                    )?.quantity ?? 1}
                                  </span>

                                  <Button
                                    size="icon"
                                    variant="outline"
                                    type="button"
                                    className="size-9"
                                    onClick={(e) => {
                                      e.preventDefault()

                                      const categoryInfo =
                                        product?.add_on_categories?.find(
                                          (cat: any) =>
                                            cat?.id === category?.category_id,
                                        )

                                      if (categoryInfo?.max_limit) {
                                        // Calculate total quantity of all addons in this category
                                        const currentTotal =
                                          selectedAddons[
                                            category?.category_id
                                          ]?.reduce(
                                            (sum: number, item: any) =>
                                              sum + (item.quantity || 1),
                                            0,
                                          ) || 0

                                        if (
                                          currentTotal >= categoryInfo.max_limit
                                        ) {
                                          toast.error(
                                            `Maximum ${categoryInfo.max_limit} item${categoryInfo.max_limit > 1 ? 's' : ''} allowed for ${category?.category_name}`,
                                          )
                                          return
                                        }
                                      }

                                      setSelectedAddons((prev: any) => ({
                                        ...prev,
                                        [category?.category_id]: prev[
                                          category?.category_id
                                        ].map((item: any) =>
                                          item.addon_price_id ===
                                            addon?.default_price?.price_id
                                            ? {
                                              ...item,
                                              quantity:
                                                (item.quantity || 1) + 1,
                                            }
                                            : item,
                                        ),
                                      }))
                                    }}
                                  >
                                    <Plus className="size-4" strokeWidth={1.5} />
                                  </Button>
                                </div>
                              )}
                          </Label>
                        </div>
                      )
                    })}
                  </TabsContent>
                )
              })}
            </div>
          </Tabs>
        </div>
      )}

      <div className="flex w-full pt-3">
        <Button
          variant="brand"
          className="w-full gap-3 shadow-card"
          size="lg"
          type="submit"
          disabled={isFetchingStockPrices}
        >
          <BagCheck className="size-5" strokeWidth={1.5} />
          <span>Add to Cart</span>
          <KeyChip variant="dark" shortcut="enter" />
        </Button>
      </div>
    </form>
  )
}
